import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FileRejection, useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { faPalette } from '@fortawesome/pro-light-svg-icons/faPalette';
import { faUpload } from '@fortawesome/pro-light-svg-icons/faUpload';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { LoadingIcon } from '../../../../common/views/LoadingIcon';
import { usePatchCatalogItemMutation, usePostFileMutation } from '../../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { useIsNativeEmbed } from '../../../../music24/navigation/hooks/useIsNativeEmbed';
import { useOpenCoverEditor } from '../../../../coverEditor/entry/useOpenCoverEditor';
import { offersCoverEditor } from '../../../../coverEditor/entry/coverFieldState';
import { addSongMessages } from '../../../../onboarding/addSong/addSongMessages';
import { catalogArtworkSrc } from '../../catalogArtwork';
import { addSongFlowMessages } from '../addSongFlowMessages';
import { itemHasCover } from '../addSongFlowSteps';
import { TrackSave } from '../useSavingCount';
import { StepHeader } from '../StepHeader';
import { StepActions } from '../StepActions';
import { useIsTouchDevice } from '../useIsTouchDevice';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from '../CatalogAddSong.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CoverStepProps {
  accountId: number;
  item: CatalogItem;
  eyebrow: string;
  track: TrackSave;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /**
   * Set when the flow came from "Which song is this cover for?" (`from=coverMaker`): the design way
   * puts the carried design of the artwork maker on this song in place of a new design.
   */
  onUseCarried?: () => void;
}

/** The upload limit of music24-api (`MAX_UPLOAD_BYTES` in `src/app/middlewares/multer.ts`). */
const COVER_MAX_MB = 25;
const BYTES_PER_MB = 1024 * 1024;
const COVER_MAX_BYTES = COVER_MAX_MB * BYTES_PER_MB;

type CoverError =
  | { kind: 'tooBig'; sizeMb: number }
  | { kind: 'wrongType' }
  | { kind: 'notImage' }
  | { kind: 'upload' };

/**
 * A refused upload with a reason the user can fix: the API answers 400 when the bytes are not an
 * image. "Try again" with the same file never works, so the step says what is wrong (QA brk-12).
 */
function coverErrorOf(failure: unknown): CoverError {
  return failure instanceof FYMAPIError && failure.statusCode === 400 ? { kind: 'notImage' } : { kind: 'upload' };
}

/**
 * Step 4 (design catalog-add-song d1-d5): the square slot where the cover appears, with "Upload
 * cover" and "Design a cover" side by side. The upload goes to `POST .../files`, then
 * `PATCH .../catalog/:uuid { artworkFileId }`. "Design a cover" opens the cover editor of this song,
 * which comes back here. The app web view shows only the upload (`offersCoverEditor`).
 */
const CoverStepComponent: React.FC<CoverStepProps> = ({
  accountId,
  item,
  eyebrow,
  track,
  onBack,
  onNext,
  onSkip,
  onUseCarried,
}) => {
  const intl = useIntl();
  const isTouch = useIsTouchDevice();
  const isNativeEmbed = useIsNativeEmbed();
  const canDesign = offersCoverEditor({ isNativeEmbed });
  const openCoverEditor = useOpenCoverEditor({ accountId });
  const [postFile] = usePostFileMutation();
  const [patchItem] = usePatchCatalogItemMutation();
  const [preview, setPreview] = useState<string>();
  const [isUploading, setUploading] = useState(false);
  const [error, setError] = useState<CoverError>();
  const hasCover = itemHasCover(item);
  const artworkSrc = catalogArtworkSrc(item, 240);

  useEffect(
    () => () => {
      if (preview !== undefined) {
        URL.revokeObjectURL(preview);
      }
    },
    [preview]
  );

  const onDropAccepted = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (file === undefined) {
        return;
      }
      setError(undefined);
      setPreview(URL.createObjectURL(file));
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file, file.name);
      track(
        postFile({ accountId, formData })
          .unwrap()
          .then((saved) => patchItem({ accountId, itemUuid: item.uuid, artworkFileId: saved.id }).unwrap())
      )
        .catch((uploadError: unknown) => {
          console.error(uploadError);
          setError(coverErrorOf(uploadError));
        })
        .finally(() => {
          setUploading(false);
          setPreview(undefined);
        });
    },
    [accountId, item.uuid, patchItem, postFile, track]
  );

  const onDropRejected = useCallback((rejections: FileRejection[]) => {
    const rejection = rejections[0];
    if (rejection === undefined) {
      return;
    }
    const isTooBig = rejection.errors.some((one) => one.code === 'file-too-large');
    setError(
      isTooBig ? { kind: 'tooBig', sizeMb: Math.ceil(rejection.file.size / BYTES_PER_MB) } : { kind: 'wrongType' }
    );
  }, []);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: { 'image/png': ['.png'], 'image/jpeg': ['.jpeg', '.jpg'], 'image/webp': ['.webp'] },
    maxSize: COVER_MAX_BYTES,
    multiple: false,
    noClick: true,
    disabled: isUploading,
  });

  const design = useCallback(() => {
    if (onUseCarried !== undefined) {
      onUseCarried();
      return;
    }
    openCoverEditor(item.uuid);
  }, [item.uuid, onUseCarried, openCoverEditor]);
  const isCarried = onUseCarried !== undefined;

  let errorText: string | undefined;
  if (error?.kind === 'tooBig') {
    errorText = intl.formatMessage(addSongMessages.errorTooBig, { size: error.sizeMb, max: COVER_MAX_MB });
  } else if (error?.kind === 'wrongType') {
    errorText = intl.formatMessage(addSongMessages.errorWrongType);
  } else if (error?.kind === 'notImage') {
    errorText = intl.formatMessage(addSongMessages.errorNotImage);
  } else if (error?.kind === 'upload') {
    errorText = intl.formatMessage(addSongMessages.errorUpload);
  }

  const shown = preview ?? (hasCover ? artworkSrc : undefined);
  const showDone = hasCover && !isUploading;

  return (
    <>
      <StepHeader
        eyebrow={eyebrow}
        question={intl.formatMessage(
          showDone ? addSongFlowMessages.coverDoneQuestion : addSongFlowMessages.coverQuestion
        )}
        sub={intl.formatMessage(addSongFlowMessages.coverSub)}
      />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <div className={styles.coverGrid} {...getRootProps()}>
        <input
          // eslint-disable-next-line react/jsx-props-no-spreading -- the dropzone input props
          {...getInputProps()}
          // The hidden file input is no control of its own: the buttons open it (QA imp-11).
          aria-hidden="true"
          tabIndex={-1}
          data-testid="add-song-cover-input"
        />
        <button
          type="button"
          className={clsx(styles.slot, shown !== undefined && styles.slotFilled, error !== undefined && styles.slotBad)}
          onClick={open}
          aria-label={intl.formatMessage(addSongFlowMessages.coverSlotLabel)}
          data-testid="add-song-cover-slot"
        >
          {shown !== undefined ? (
            <img src={shown} alt="" />
          ) : (
            <span className={styles.slotLabel}>
              <FontAwesomeIcon icon={faImage} size="2x" />
              {intl.formatMessage(addSongFlowMessages.coverSlot)}
              <span>3000 × 3000 px</span>
            </span>
          )}
          {isUploading && (
            <span className={styles.veil}>
              <LoadingIcon small />
            </span>
          )}
        </button>
        <div className={styles.ways}>
          {!showDone && (
            <>
              <div className={clsx(styles.way, styles.wayDrop, (isDragActive || isUploading) && styles.wayHot)}>
                <span className={styles.kbox}>
                  <FontAwesomeIcon icon={faUpload} />
                </span>
                <div className={styles.wayText}>
                  <b>
                    {intl.formatMessage(
                      isUploading ? addSongFlowMessages.coverUploading : addSongFlowMessages.coverUpload
                    )}
                  </b>
                  <small>
                    {intl.formatMessage(
                      isTouch ? addSongFlowMessages.coverUploadBodyTouch : addSongFlowMessages.coverUploadBody,
                      { max: COVER_MAX_MB }
                    )}
                  </small>
                </div>
                <SMLButton outline omitCap onClick={open} disabled={isUploading} testID="add-song-cover-upload">
                  {intl.formatMessage(addSongFlowMessages.coverUpload)}
                </SMLButton>
              </div>
              {canDesign && (
                <div className={styles.way}>
                  <span className={clsx(styles.kbox, styles.kboxBrand)}>
                    <FontAwesomeIcon icon={faPalette} />
                  </span>
                  <div className={styles.wayText}>
                    <b>
                      {intl.formatMessage(
                        isCarried ? addSongFlowMessages.coverCarriedTitle : addSongFlowMessages.coverDesignTitle
                      )}
                    </b>
                    <small>
                      {intl.formatMessage(
                        isCarried ? addSongFlowMessages.coverCarriedBody : addSongFlowMessages.coverDesignBody
                      )}
                    </small>
                  </div>
                  <SMLButton
                    outline={!isCarried}
                    omitCap
                    onClick={design}
                    disabled={isUploading}
                    testID="add-song-cover-design"
                  >
                    {intl.formatMessage(
                      isCarried ? addSongFlowMessages.coverUseCarried : addSongFlowMessages.coverDesign
                    )}
                  </SMLButton>
                </div>
              )}
            </>
          )}
          {showDone && (
            <>
              <div className={styles.way}>
                <span className={clsx(styles.kbox, styles.kboxOk)}>
                  <FontAwesomeIcon icon={faCheck} />
                </span>
                <div className={styles.wayText}>
                  <b>{intl.formatMessage(addSongFlowMessages.coverOnSong)}</b>
                </div>
              </div>
              <div className={styles.way}>
                <div className={styles.wayText}>
                  <b>{intl.formatMessage(addSongFlowMessages.coverDifferent)}</b>
                  <small>{intl.formatMessage(addSongFlowMessages.coverReplaces)}</small>
                </div>
                <SMLButton size="sm" outline omitCap onClick={open}>
                  {intl.formatMessage(addSongFlowMessages.coverReplaceFile)}
                </SMLButton>
                {canDesign && (
                  <SMLButton size="sm" outline omitCap onClick={design}>
                    {intl.formatMessage(
                      isCarried ? addSongFlowMessages.coverUseCarried : addSongFlowMessages.coverDesignNew
                    )}
                  </SMLButton>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {errorText !== undefined && (
        <div className={styles.alertError} role="alert">
          {errorText}
        </div>
      )}
      <StepActions
        onBack={onBack}
        onSkip={onSkip}
        disabled={isUploading}
        // A Next that is off says why (QA imp-3).
        offHint={hasCover || isUploading ? undefined : intl.formatMessage(addSongFlowMessages.coverNextHint)}
        main={{ label: intl.formatMessage(addSongFlowMessages.next), onClick: onNext, disabled: !hasCover }}
      />
    </>
  );
};

export const CoverStep = TypedMemo(CoverStepComponent);
